import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import Cookies from 'universal-cookie';
import { useTranslation } from 'react-i18next';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import { API_GET, DBRequest, useConfig } from '../../../api/api';
import ExoDialog from '../../../components/exo/ExoDialog';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import Feedback from '../../../components/special/Feedback';
import { numberToString } from '../../../components/special/numberConverter';
import { tokens } from '../../../global/theme/tokens';

const TravelExpenseExportDialog = ({ open, onClose, project }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const teamUsers = project?.teamMembers || [];

  const [selectedUsers, setSelectedUsers] = useState([]);
  const [presets, setPresets] = useState(null);
  const [selectedPresets, setSelectedPresets] = useState([]);
  const [localDateStart, setLocalDateStart] = useState(null);
  const [localDateEnd, setLocalDateEnd] = useState(null);
  const [isExporting, setIsExporting] = useState(false);

  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: '',
  });
  function handleAlertOpen(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
  }

  const dateFormat = config?.general?.dateOutputFormat || 'YYYY-MM-DD';

  // An empty selection would be sent as "no filter" and export everything, so
  // the export stays blocked until at least one entry of each group is picked.
  const missingSelection =
    (teamUsers.length > 0 && selectedUsers.length === 0) ||
    (Boolean(presets?.length) && selectedPresets.length === 0);

  useEffect(() => {
    if (!open) return;
    setLocalDateStart(null);
    setLocalDateEnd(null);
    setSelectedUsers(teamUsers.map((u) => u.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !config || !project) return;
    DBRequest({
      config,
      path: `travel-expense-presets?projectId[eq]=${project.id}&perPage=500`,
      method: API_GET,
      onResponse: (res) => {
        const list = res.data || [];
        setPresets(list);
        setSelectedPresets(list.map((p) => p.id));
      },
    });
  }, [open, config, project]);

  const toggleId = (setter) => (id) =>
    setter((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  const toggleUser = toggleId(setSelectedUsers);
  const togglePreset = toggleId(setSelectedPresets);

  const handleSelectAllUsers = () =>
    setSelectedUsers(
      selectedUsers.length === teamUsers.length ? [] : teamUsers.map((u) => u.id)
    );
  const handleSelectAllPresets = () =>
    setSelectedPresets(
      presets && selectedPresets.length === presets.length
        ? []
        : (presets || []).map((p) => p.id)
    );

  function presetLabel(preset) {
    const address = preset.destinationAddress;
    const location = address
      ? [address.zipCode, address.city].filter(Boolean).join(' ') ||
        address.street
      : null;
    return [location, `${numberToString(preset.distanceKilometers, 2)} km`]
      .filter(Boolean)
      .join(' · ');
  }

  function buildExportUrl() {
    const params = new URLSearchParams();
    params.append('projectId[eq]', project.id);
    if (localDateStart) {
      params.append('startTime[gte]', localDateStart.format('YYYY-MM-DD'));
    }
    if (localDateEnd) {
      params.append(
        'startTime[lte]',
        localDateEnd.format('YYYY-MM-DD') + ' 23:59:59'
      );
    }
    selectedUsers.forEach((id) => params.append('userIds[]', id));
    selectedPresets.forEach((id) => params.append('travelExpensePresetIds[]', id));
    return `${config.API_URL}export-travel-expenses?${params.toString()}`;
  }

  async function handleExport() {
    setIsExporting(true);
    const cookies = new Cookies();
    try {
      const response = await fetch(buildExportUrl(), {
        method: 'GET',
        headers: { Authorization: `Bearer ${cookies.get('accessToken')}` },
      });
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `reisekosten_${project.id}_${moment().format(
        'YYYY-MM-DD'
      )}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      onClose();
    } catch (error) {
      console.error('Export error:', error);
      handleAlertOpen(t('Export failed'), 'error');
    } finally {
      setIsExporting(false);
    }
  }

  const getSelectableStyle = (isSelected) => ({
    cursor: 'pointer',
    borderRadius: '12px',
    border: `1px solid ${isSelected ? colors.info + '80' : colors.grey[600]}`,
    backgroundColor: isSelected ? colors.info + '30' : 'transparent',
    transition: 'all 0.2s ease',
    '&:hover': {
      borderColor: colors.info + '80',
      backgroundColor: isSelected ? colors.info + '40' : colors.info + '15',
    },
  });

  return (
    <ExoDialog open={open} onClose={onClose} limitWidth maxWidth="650px">
      <Box className="flex flex-col gap-5 relative h-full overflow-y-auto">
        <Typography variant="h3" textAlign="center" className="py-2">
          {t('Reisekosten exportieren')}
        </Typography>

        {/* Timespan */}
        <Box className="p-4 rounded-xl" sx={{ backgroundColor: colors.bgInput }}>
          <Box className="flex justify-between items-center mb-3">
            <Typography variant="h5">{t('Zeitraum')}</Typography>
            <Button
              size="small"
              onClick={() => {
                setLocalDateStart(null);
                setLocalDateEnd(null);
              }}
              disabled={!localDateStart && !localDateEnd}
            >
              {t('Gesamter Zeitraum')}
            </Button>
          </Box>
          <Box className="flex flex-row gap-3 w-full items-center">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DatePicker
                className="w-full"
                label={t('Von')}
                value={localDateStart}
                onChange={setLocalDateStart}
                format={dateFormat}
              />
              <Typography sx={{ color: colors.grey[500] }}>–</Typography>
              <DatePicker
                className="w-full"
                label={t('Bis')}
                value={localDateEnd}
                onChange={setLocalDateEnd}
                format={dateFormat}
              />
            </LocalizationProvider>
          </Box>
        </Box>

        {/* Team members */}
        <Box>
          <Box className="flex justify-between items-center mb-3">
            <Typography variant="h5">{t('Teammitglieder')}</Typography>
            {teamUsers.length > 0 && (
              <Button size="small" onClick={handleSelectAllUsers}>
                {selectedUsers.length === teamUsers.length
                  ? t('Alle abwählen')
                  : t('Alle auswählen')}
              </Button>
            )}
          </Box>
          {teamUsers.length > 0 ? (
            <Box className="flex flex-wrap gap-2">
              {teamUsers.map((user) => {
                const isSelected = selectedUsers.includes(user.id);
                return (
                  <Box
                    key={user.id}
                    onClick={() => toggleUser(user.id)}
                    className="flex items-center gap-2 px-3 py-2"
                    sx={getSelectableStyle(isSelected)}
                  >
                    <ExoAvatar
                      name={`${user.firstName} ${user.lastName}`}
                      picture={user.picture?.temporaryUrl}
                      size="32px"
                    />
                    <Typography variant="body2">
                      {user.firstName} {user.lastName}
                    </Typography>
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{ fontSize: 18, color: colors.info }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: colors.grey[500] }}>
              {t('No team members')}
            </Typography>
          )}
        </Box>

        {/* Destinations (presets) */}
        <Box>
          <Box className="flex justify-between items-center mb-3">
            <Typography variant="h5">{t('Destinations')}</Typography>
            {presets && presets.length > 0 && (
              <Button size="small" onClick={handleSelectAllPresets}>
                {selectedPresets.length === presets.length
                  ? t('Alle abwählen')
                  : t('Alle auswählen')}
              </Button>
            )}
          </Box>
          {!presets ? (
            <Box className="flex justify-center py-6">
              <CircularProgress size={28} />
            </Box>
          ) : presets.length > 0 ? (
            <Box className="flex flex-wrap gap-2">
              {presets.map((preset) => {
                const isSelected = selectedPresets.includes(preset.id);
                return (
                  <Box
                    key={preset.id}
                    onClick={() => togglePreset(preset.id)}
                    className="flex items-center gap-2 px-3 py-2"
                    sx={getSelectableStyle(isSelected)}
                  >
                    <DirectionsCarIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                    <Typography variant="body2">
                      {presetLabel(preset)}
                    </Typography>
                    {isSelected && (
                      <CheckCircleIcon
                        sx={{ fontSize: 18, color: colors.info }}
                      />
                    )}
                  </Box>
                );
              })}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: colors.grey[500] }}>
              {t('No data available')}
            </Typography>
          )}
        </Box>

        {/* Actions */}
        <Box className="flex flex-col items-end gap-1 pt-3">
          {missingSelection && (
            <Typography variant="caption" sx={{ color: colors.grey[500] }}>
              {t('Select at least one team member and one destination')}
            </Typography>
          )}
          <Box className="flex flex-row w-full items-center justify-between">
            <Button
              variant="outlined"
              color="error"
              onClick={onClose}
              disabled={isExporting}
            >
              {t('Abbrechen')}
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={handleExport}
              disabled={isExporting || missingSelection}
              startIcon={
                isExporting ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  <FileDownloadIcon />
                )
              }
            >
              {t('CSV exportieren')}
            </Button>
          </Box>
        </Box>
      </Box>
      <Feedback state={alertState} setState={setAlertState} />
    </ExoDialog>
  );
};

export default TravelExpenseExportDialog;
